import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconDefinition, faEnvelope, faEnvelopeOpen, faFilter, faListOl } from '@fortawesome/pro-duotone-svg-icons';
import { faBan, faParking, faStar as faSolidStar, faStarHalfAlt, faUser } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import { iif } from '@ngxs/store/operators';
import { CellClickEvent, SelectionEvent } from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { SortDescriptor } from '@progress/kendo-data-query';
import _ from 'lodash';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { getTopmostTableCellElement, isTargetingGridHeader } from 'src/app/helper/elementAncestorFunctions';
import { shortcutStringBuilder } from 'src/app/helper/shortcutStringBuilder';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { Key } from 'src/app/samwebDescription/samweb/shortcuts/Keys';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { EmailBehaviourState } from 'src/app/store/samwin/email/email-behaviour/email-behaviour.state';
import { MessageInfo } from 'src/app/store/samwin/samwin-mail-list/message-info';
import {
   GetMessageContent,
   GetSearchedMessages,
   MessageListChangeCustomSorting,
   MessageListEntrySelected,
   MessageListSelectNextEntry,
   OpenMessage,
   SearchValueChanged,
   UpdateMessageDraggedStatus,
} from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { SamwinMessageListStateSelectors } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.selectors';
import { SamwinMessageListState } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.state';
import { SamwinMessageSkillBrowserState } from 'src/app/store/samwin/samwin-mail-skill-browser/samwin-mail-skill-browser.state';
import { MailingFolderEnum } from 'src/app/store/samwin/samwin-skills/mailing-folders-enum';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { RestoreWindow, SetWindowIndex } from 'src/app/store/utility/samweb-window/samweb-window.actions';
import { SamwebWindowState, WindowTypes } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { BasicBrickComponent } from '../../../basic-brick-component';
import { SamwebBrick } from '../../../samweb-brick.decorator';

@Component({
   selector: 'samweb-mail-waitlist',
   templateUrl: './mail-waitlist.component.html',
   styleUrls: ['./mail-waitlist.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'mailWaitlist',
   brickTitleTranslationKey: 'brick.brick_definitions.mail_wait_list.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.mail_wait_list.description',
   brickIcon: faListOl,
   brickSubIcon: faEnvelope,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.MAILING,
   requiredFeature: FeatureEnum.SAMWIN_7_EMAIL,
})
export class MailWaitlistComponent extends BasicBrickComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject();

   @Select(SamwinMessageListStateSelectors.getFilteredMessageList)
   public filteredMessageList$: Observable<MessageInfo[]>;

   @Select(SamwinMessageListState.isFilteredBySkill)
   isFilteredBySkill$: Observable<boolean>;

   @Select(SamwinMessageListState.getFilteredSkill)
   filteredSkill$: Observable<SkillGroup>;

   @Select(SamwinMessageListState.getFilteredGroupTranslationValue)
   filteredGroupTranslationValue$: Observable<string>;

   @Select(SamwinMessageListState.getGroupFilter)
   filteredGroup$: Observable<string>;

   @Select(SamwinMessageListState.getSearchText)
   searchTextValue$: Observable<string>;

   @Select(SamwinMessageListState.getSearchValue)
   searchValue$: Observable<string>;

   @Select(SamwinMessageListState.isInboxSelected)
   isInboxSelected$: Observable<boolean>;

   @Select(SamwinMessageListState.isOutboxSelected)
   isOutboxSelected$: Observable<boolean>;

   @Select(SamwinMessageListState.wasSearchEverExecuted)
   public wasSearchEverExecuted$: Observable<boolean>;

   @Select(SamwinMessageListState.getSortDescription)
   public sortDescription$: Observable<SortDescriptor[]>;

   @Select(SamwinMessageListState.canMessageBeDropped)
   public isMessageDroppable$: Observable<boolean>;

   @Select(SamwinMessageListState.isMessageDragged)
   public isMessageDragged$: Observable<boolean>;

   @Select(SamwinMessageSkillBrowserState.getMailingTreeviewKeys)
   public MailingSkillDropList$: Observable<string[]>;

   @Select(SamwinMessageListState.selectedRowIDs)
   public selectedKeys$: Observable<string[]>;

   public skillIcon: IconDefinition = faStarHalfAlt;
   public envelopeIcon: IconDefinition = faEnvelope;
   public banIcon: IconDefinition = faBan;
   public statusBarFilterIcon: IconDefinition = faFilter;

   public InboxKey: string = MailingFolderEnum.INBOX.name;
   public OutboxKey: string = MailingFolderEnum.OUTBOX.name;
   public TrackingKey: string = MailingFolderEnum.TRACKING.name;
   public SentKey: string = MailingFolderEnum.SENT.name;
   public DeletedKey: string = MailingFolderEnum.DELETED.name;
   public CompletedKey: string = MailingFolderEnum.COMPLETED.name;

   @ViewChild('cellTooltipDirective')
   public cellTooltipDirective: TooltipDirective;

   public parkingIconSolid = faParking;
   public userIconSolid = faUser;
   public skillIconSolid = faSolidStar;

   public mailIcon: IconDefinition = faEnvelope;
   public openedMailIcon: IconDefinition = faEnvelopeOpen;

   get gridContent(): Element {
      return _(this.element.nativeElement.getElementsByClassName('k-grid-content')).head();
   }
   get gridDisplayHeight(): number {
      return this.element.nativeElement.querySelector('.k-virtual-content')?.offsetHeight;
   }
   get gridRowHeight(): number {
      return this.element.nativeElement.querySelector('td')?.offsetHeight ?? 0;
   }
   get gridPageSize(): number {
      return _.round(this.gridDisplayHeight / this.gridRowHeight);
   }

   constructor(public store: Store, private element: ElementRef) {
      super();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnInit(): void {
      combineLatest([
         this.store.select(SamwinMessageListState.getSearchValue),
         this.store.select(SamwinMessageListState.getGroupFilter),
         this.store.select(SamwinMessageListState.getSkillFilter),
      ])
         .pipe(
            tap(([searchValue, folder, skill]) =>
               iif(() => searchValue.length > 0, this.store.dispatch(new GetSearchedMessages(searchValue, folder, skill)), of(null))
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      combineLatest([
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowUp),
            action: () => {
               console.debug('[HotKey] Mail Waitlist Pressed ArrowUp');
               this.store.dispatch(new MessageListSelectNextEntry('up'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowDown),
            action: () => {
               console.debug('[HotKey] Mail Waitlist Pressed ArrowDown');
               this.store.dispatch(new MessageListSelectNextEntry('down'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageUp),
            action: () => {
               console.debug('[HotKey] Mail Waitlist Pressed PageUp');
               this.store.dispatch(new MessageListSelectNextEntry('up', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageDown),
            action: () => {
               console.debug('[HotKey] Mail Waitlist Pressed PageDown');
               this.store.dispatch(new MessageListSelectNextEntry('down', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.Enter),
            action: () => {
               console.debug('[HotKey] Mail Waitlist Pressed Enter');
               const message = this.store.selectSnapshot(SamwinMessageListStateSelectors.getSelectedRow);
               if (!_.isNil(message)) {
                  console.debug('[HotKey] execute');
                  if (this.store.selectSnapshot(SamwinMessageListState.getGroupFilter) === this.InboxKey) {
                     this.store.dispatch(new OpenMessage(message.callId, message.messageId));
                  } else {
                     this.store.dispatch(new GetMessageContent(message.messageId));
                  }
               }
               return true;
            },
         }),
      ])
         .pipe(
            tap((shortcuts) => {
               this.keyActions = shortcuts;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   public handleFilterChange(query: string): void {
      this.store.dispatch(new SearchValueChanged(query));
   }

   public sortChange(sortDescription: SortDescriptor[]) {
      this.store.dispatch(new MessageListChangeCustomSorting(sortDescription));
   }

   onRowDoubleClick(event: MouseEvent) {
      if (isTargetingGridHeader(event)) {
         return;
      }
      const message = this.store.selectSnapshot(SamwinMessageListStateSelectors.getSelectedRow);
      if (_.isNil(message)) {
         return;
      }
      const isOpen = this.store.selectSnapshot(EmailBehaviourState.isEmailOpened)(message.messageId);
      if (message.messageStatus.isCreated || isOpen) {
         const index = this.store.selectSnapshot(SamwebWindowState.getHighestIndex);
         this.store.dispatch(new SetWindowIndex(WindowTypes.Email, message.messageId, index + 1));
         this.store.dispatch(new RestoreWindow(WindowTypes.Email, message.messageId));
         return;
      }
      if (this.store.selectSnapshot(SamwinMessageListState.getGroupFilter) === this.InboxKey) {
         this.store.dispatch(new OpenMessage(message.callId, message.messageId));
      } else {
         this.store.dispatch(new GetMessageContent(message.messageId));
      }
   }

   onCellClick(e: CellClickEvent) {
      this.store.dispatch(new MessageListEntrySelected(e?.dataItem?.messageId));
   }

   dragStart(event) {
      this.store.dispatch(new UpdateMessageDraggedStatus(true));
   }

   dragEnd(event) {
      this.store.dispatch(new UpdateMessageDraggedStatus(false));
   }

   public showTooltip(e: MouseEvent): void {
      const element = e.target as HTMLElement;
      const tooltipAnchor = getTopmostTableCellElement(element);
      if (tooltipAnchor && (tooltipAnchor.scrollWidth > tooltipAnchor.parentElement.offsetWidth || tooltipAnchor.offsetWidth < tooltipAnchor.scrollWidth)) {
         this.cellTooltipDirective.toggle(tooltipAnchor);
      } else {
         this.cellTooltipDirective.hide();
      }
   }

   public hideTooltip() {
      this.cellTooltipDirective.hide();
   }

   selectionChanged(event: SelectionEvent) {
      const selectedRow = _.first(event?.selectedRows);
      if (_.isNil(selectedRow)) {
         return;
      }
      const selectedEntry = selectedRow.dataItem as MessageInfo;
      this.store.dispatch(new MessageListEntrySelected(selectedEntry.messageId));
   }

   scrollToSelectedRow() {
      const index: number = this.store.selectSnapshot(SamwinMessageListStateSelectors.getSelectedRowIndex);
      const selectedRowPosition = this.gridRowHeight * index;
      const halfWindowheight = this.gridDisplayHeight / 2 - this.gridRowHeight / 2;
      const scrollPosition = selectedRowPosition - halfWindowheight;
      this.gridContent.scrollTo({ top: scrollPosition });
   }
}
